import React, {useEffect, useState} from 'react';
import momentZ from 'moment-timezone';
import MDSpinner from 'react-md-spinner';
import ReactJson from 'react-json-view';
import {requestMetricsWebsocket} from '../../utils/MetricsWebsocket';

const money = (value, currency = 'USD') => (value === null || value === undefined ? '—' :
  `${Number(value).toFixed(2)} ${currency}`);

const OUTCOME_CLASS = {
  REFUNDED: 'refund-saver-badge refund-saver-badge-refunded',
  NOT_REFUNDED: 'refund-saver-badge refund-saver-badge-saved',
  REVERSED: 'refund-saver-badge refund-saver-badge-saved',
  PENDING: 'refund-saver-badge',
};

const JsonBlock = ({title, value}) => <div className={'refund-saver-json'}>
  <div className={'refund-saver-json-title'}>{title}</div>
  {value === null || value === undefined ?
    <div className={'refund-saver-muted'}>—</div> :
    <ReactJson src={typeof value === 'object' ? value : {value}} displayDataTypes={false} collapsed={2} name={false}/>}
</div>;

const CaseDetails = ({id, onClose}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setData(null);
    setError(null);
    requestMetricsWebsocket('REFUND_SAVER_CASE', {id}, 'REFUND_SAVER_CASE_RESULT')
        .then((result) => setData(result.case))
        .catch((e) => setError(e.message));
  }, [id]);

  return <div className={'refund-saver-details'}>
    <div className={'refund-saver-details-header'}>
      <div className={'aso-report-item-header'}>Case {id}</div>
      <button className={'onelight-second-btn'} onClick={onClose}>Close</button>
    </div>
    {error && <div className={'refund-saver-error'}>{error}</div>}
    {!data && !error && <div className={'spinner-container'}><MDSpinner size={40} singleColor={'#00DADA'}/></div>}
    {data && <>
      <div className={'refund-saver-details-grid'}>
        <div><span>Platform</span>{data.platform}</div>
        <div><span>App</span>{data.appId || '—'}</div>
        <div><span>Package</span>{data.packageName || '—'}</div>
        <div><span>Product</span>{data.productId || '—'}</div>
        <div><span>Price</span>{money(data.priceUsd)} {data.priceLocal ? `(${money(data.priceLocal, data.currency || '')})` : ''}</div>
        <div><span>User</span>{data.userId || '—'} {data.deviceId ? `· ${data.deviceId}` : ''}</div>
        <div><span>Decision</span>{data.refundPreference || '—'} · {data.decisionRule || '—'}{data.ambiguous ? ' · ambiguous' : ''}</div>
        <div><span>Status</span>{data.status}{data.sentAt ? ` · ${momentZ(data.sentAt).format('YYYY-MM-DD HH:mm')}` : ''}</div>
        <div><span>Outcome</span>{data.outcome}{data.outcomeAt ? ` · ${momentZ(data.outcomeAt).format('YYYY-MM-DD HH:mm')}` : ''}</div>
        <div><span>Requested</span>{momentZ(data.createdAt).format('YYYY-MM-DD HH:mm')}</div>
        {data.errorMessage && <div><span>Error</span>{data.errorMessage}</div>}
      </div>
      <JsonBlock title={'Why this decision (decision_details)'} value={data.decisionDetails}/>
      <JsonBlock title={'Sent to the store (request_body)'} value={data.requestBody}/>
      <JsonBlock title={'Store response (store_response)'} value={data.storeResponse}/>
      <JsonBlock title={'Store outcome event (outcome_event)'} value={data.outcomeEvent}/>
    </>}
  </div>;
};

// Cases of the selected chart bucket; a click opens the full case with its jsonb columns.
export const RefundSaverCases = ({params, bucket, group}) => {
  const [cases, setCases] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setCases(null);
    setError(null);
    setSelectedId(null);
    requestMetricsWebsocket('REFUND_SAVER_CASES', {...params, group, bucket}, 'REFUND_SAVER_CASES_RESULT')
        .then((result) => setCases(result))
        .catch((e) => setError(e.message));
  }, [JSON.stringify(params), bucket, group]);

  return <div className={'aso-report-data-table refund-saver-cases'}>
    <div className={'aso-report-item-header'} style={{paddingBottom: '10px'}}>
      Refund requests · {group} of {momentZ(bucket).format('YYYY-MM-DD')}
      {cases && cases.hasMore ? ` (first ${cases.limit})` : ''}
    </div>
    {error && <div className={'refund-saver-error'}>{error}</div>}
    {!cases && !error && <div className={'spinner-container'}><MDSpinner size={40} singleColor={'#00DADA'}/></div>}
    {cases && cases.cases.length === 0 && <div className={'refund-saver-muted'}>No refund requests in this {group}</div>}
    {cases && cases.cases.length > 0 && <div className={'refund-saver-table'}>
      <div className={'aso-report-header refund-saver-row refund-saver-row-header'}>
        <div>Requested</div><div>App</div><div>Product</div><div>Price</div><div>Decision</div><div>Status</div><div>Outcome</div>
      </div>
      {cases.cases.map((c) => <div
        key={c.id}
        className={'refund-saver-row' + (c.id === selectedId ? ' refund-saver-row-active' : '')}
        onClick={() => setSelectedId(c.id)}
      >
        <div>{momentZ(c.createdAt).format('YYYY-MM-DD HH:mm')}</div>
        <div>{c.appId || '—'} · {c.platform}</div>
        <div title={c.productId}>{c.productId || '—'}</div>
        <div>{money(c.priceUsd)}</div>
        <div>{c.refundPreference || '—'}{c.ambiguous ? ' ⚠️' : ''}</div>
        <div>{c.status}</div>
        <div><span className={OUTCOME_CLASS[c.outcome] || 'refund-saver-badge'}>{c.outcome}</span></div>
      </div>)}
    </div>}
    {selectedId && <CaseDetails id={selectedId} onClose={() => setSelectedId(null)}/>}
  </div>;
};
